import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, Star, Award, BookOpen, Briefcase, Globe } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const JOURNEE_LOGO = "https://static.prod-images.emergentagent.com/jobs/a27b2f16-dbd8-4903-b7cf-e0c3f4166774/images/def00b15247db958e0a1a006c0644ef388493b1419b5bc5be44742dac8edad62.png";

export const RussophonyDaySection = () => {
  const { language } = useLanguage();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Calculate next September 26th (starting from 2027)
  const getNextRussophonyDay = () => {
    const now = new Date();
    // Event starts in 2027
    let targetYear = 2027;
    let targetDate = new Date(targetYear, 8, 26, 0, 0, 0);
    
    // If we're past Sept 26 2027, calculate next year
    if (now > targetDate) {
      targetYear = now.getFullYear();
      targetDate = new Date(targetYear, 8, 26, 0, 0, 0);
      if (now > targetDate) {
        targetDate = new Date(targetYear + 1, 8, 26, 0, 0, 0);
      }
    }
    
    return targetDate;
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      const targetDate = getNextRussophonyDay();
      const now = new Date();
      const difference = targetDate - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setTimeLeft({ days, hours, minutes, seconds });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const content = {
    fr: {
      overline: "Événement Continental Annuel",
      title: "Journée Africaine de la Russophonie",
      date: "26 SEPTEMBRE",
      subtitle: "Date Officielle Annuelle — 2027",
      description: "Ce rendez-vous va commencer en 2027. Chaque 26 septembre, l'OAR organisera simultanément dans ses pays membres la Journée Africaine de la Russophonie, symbole d'unité, de coopération stratégique et d'influence continentale.",
      premiumNote: "Une grande rencontre stratégique dans un pays africain où seuls les membres Premium seront invités.",
      countdown: {
        days: "Jours",
        hours: "Heures",
        minutes: "Minutes",
        seconds: "Secondes"
      },
      objectives: {
        title: "Objectifs Stratégiques",
        items: [
          { icon: Globe, text: "Consolidation des relations Afrique–espace russophone" },
          { icon: BookOpen, text: "Promotion académique et linguistique" },
          { icon: Briefcase, text: "Forum économique et partenariats" },
          { icon: Award, text: "Diplomatie culturelle" }
        ]
      },
      identity: "Le 26 septembre : une date identitaire, un marqueur diplomatique, un instrument d'influence douce."
    },
    en: {
      overline: "Annual Continental Event",
      title: "African Day of Russophony",
      date: "SEPTEMBER 26",
      subtitle: "Official Annual Date — 2027",
      description: "This event will begin in 2027. Every September 26, OAR will simultaneously organize the African Day of Russophony in its member countries, a symbol of unity, strategic cooperation and continental influence.",
      premiumNote: "A major strategic meeting in an African country where only Premium members will be invited.",
      countdown: {
        days: "Days",
        hours: "Hours",
        minutes: "Minutes",
        seconds: "Seconds"
      },
      objectives: {
        title: "Strategic Objectives",
        items: [
          { icon: Globe, text: "Consolidation of Africa-Russophone space relations" },
          { icon: BookOpen, text: "Academic and linguistic promotion" },
          { icon: Briefcase, text: "Economic forum and partnerships" },
          { icon: Award, text: "Cultural diplomacy" }
        ]
      },
      identity: "September 26: an identity date, a diplomatic marker, a soft power instrument."
    },
    ru: {
      overline: "Ежегодное Континентальное Событие",
      title: "Африканский День Русскоязычия",
      date: "26 СЕНТЯБРЯ",
      subtitle: "Официальная Ежегодная Дата — 2027",
      description: "Это мероприятие начнётся в 2027 году. Каждое 26 сентября ОАР будет одновременно организовывать в своих странах-членах Африканский День Русскоязычия — символ единства, стратегического сотрудничества и континентального влияния.",
      premiumNote: "Большая стратегическая встреча в африканской стране, куда будут приглашены только Premium-члены.",
      countdown: {
        days: "Дней",
        hours: "Часов",
        minutes: "Минут",
        seconds: "Секунд"
      },
      objectives: {
        title: "Стратегические Цели",
        items: [
          { icon: Globe, text: "Укрепление отношений Африка–русскоязычное пространство" },
          { icon: BookOpen, text: "Академическое и языковое продвижение" },
          { icon: Briefcase, text: "Экономический форум и партнёрства" },
          { icon: Award, text: "Культурная дипломатия" }
        ]
      },
      identity: "26 сентября: идентичная дата, дипломатический маркер, инструмент мягкой силы."
    }
  };

  const t = content[language] || content.fr;

  return (
    <section
      data-testid="russophony-day-section"
      className="py-24 md:py-32 bg-oar-blue relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-oar-blue-dark via-oar-blue to-oar-blue-light opacity-50" />
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-oar-orange/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-oar-green/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.2em] text-oar-orange font-bold mb-4">
            <Calendar className="w-4 h-4" />
            {t.overline}
          </span>
          
          {/* Logo Journée de la Russophonie */}
          <div className="relative inline-block mb-6">
            <div className="bg-white p-3 rounded-xl shadow-2xl border-2 border-oar-gold">
              <img
                src={JOURNEE_LOGO}
                alt="Logo Journée Africaine de la Russophonie"
                className="h-32 w-32 md:h-40 md:w-40 object-contain"
              />
            </div>
            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 bg-oar-red text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
              {t.date}
            </div>
          </div>

          <h2
            data-testid="russophony-day-title"
            className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-4"
          >
            {t.title}
          </h2>
          <p className="text-oar-orange text-lg md:text-xl font-medium mb-6">
            {t.subtitle}
          </p>
        </div>

        {/* Countdown Timer */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="grid grid-cols-4 gap-4 md:gap-8">
            {[
              { value: timeLeft.days, label: t.countdown.days },
              { value: timeLeft.hours, label: t.countdown.hours },
              { value: timeLeft.minutes, label: t.countdown.minutes },
              { value: timeLeft.seconds, label: t.countdown.seconds },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 md:p-6 text-center"
              >
                <div className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white">
                  {String(item.value).padStart(2, '0')}
                </div>
                <div className="text-xs md:text-sm text-white/70 uppercase tracking-wider mt-2">
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <p className="text-base md:text-lg text-white/90 leading-relaxed mb-6">
            {t.description}
          </p>
          <div className="inline-flex items-center gap-2 bg-oar-red/20 border border-oar-red/50 px-6 py-3 rounded-lg">
            <Star className="w-5 h-5 text-oar-orange" />
            <span className="text-white text-sm md:text-base font-medium">
              {t.premiumNote}
            </span>
          </div>
        </div>

        {/* Strategic Objectives */}
        <div className="max-w-4xl mx-auto">
          <h3 className="font-heading text-xl md:text-2xl text-oar-orange text-center mb-8">
            {t.objectives.title}
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {t.objectives.items.map((item, index) => (
              <div
                key={index}
                className="flex items-center gap-4 bg-white/10 backdrop-blur-sm border border-white/20 p-4 rounded-lg"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-oar-orange/20 rounded-lg flex-shrink-0">
                  <item.icon className="w-5 h-5 text-oar-orange" />
                </div>
                <span className="text-white text-sm md:text-base">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Identity Statement */}
        <div className="mt-16 text-center">
          <div className="flex justify-center gap-2 mb-8">
            <div className="w-12 h-1 bg-white rounded-full" />
            <div className="w-12 h-1 bg-oar-orange rounded-full" />
            <div className="w-12 h-1 bg-oar-green rounded-full" />
          </div>
          <p className="text-white/70 italic text-sm md:text-base max-w-2xl mx-auto">
            {t.identity}
          </p>
        </div>
      </div>
    </section>
  );
};
